/**
 * GlobalSearch — header-mounted unified search box.
 *
 * Fans out a debounced query to the four primary list endpoints in parallel
 * (agreements / persons / accounts / insurance policies), groups the hits, and
 * navigates to the chosen detail page on selection. Cmd-K (Ctrl-K on Win/Linux)
 * focuses the box from anywhere in the app. Navigation is routed through the
 * dirty-form guard so unsaved edits are not silently discarded.
 *
 * Implementation notes:
 *  - Reuses existing `q` filters on each list endpoint — no new backend route.
 *  - Limit 5 per entity is enough for a header dropdown; anything more belongs
 *    on the entity's own list page (search is also primed there).
 *  - We intentionally do NOT search join tables (parties, beneficiaries, change
 *    requests) here — those are best reached via their parent's detail tab.
 */
import { Combobox, Group, Loader, Text, TextInput, useCombobox } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconBuildingBank, IconClipboardList, IconSearch, IconShieldCheck, IconUsers,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { accountInsuranceApi } from '../api/accountInsurance';
import { accountsApi } from '../api/accounts';
import { agreementsApi } from '../api/agreements';
import { personsApi } from '../api/persons';
import { useDirtyForm } from './DirtyFormProvider';

const MIN_QUERY_LEN = 2;
const PER_BUCKET_LIMIT = 5;
const DEBOUNCE_MS = 250;

export function GlobalSearch() {
  const navigate = useNavigate();
  const { confirmDiscardAsync, anyDirty } = useDirtyForm();
  const inputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search.trim(), DEBOUNCE_MS);
  const enabled = debounced.length >= MIN_QUERY_LEN;

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // Cmd-K / Ctrl-K shortcut to focus the search box.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isModK = (e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey);
      if (!isModK) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
      combobox.openDropdown();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [combobox]);

  const agreementsQ = useQuery({
    queryKey: ['globalsearch', 'agreements', debounced],
    queryFn: () => agreementsApi.list({ q: debounced, limit: PER_BUCKET_LIMIT }),
    enabled,
    staleTime: 30_000,
  });
  const personsQ = useQuery({
    queryKey: ['globalsearch', 'persons', debounced],
    queryFn: () => personsApi.list({ q: debounced, limit: PER_BUCKET_LIMIT }),
    enabled,
    staleTime: 30_000,
  });
  const accountsQ = useQuery({
    queryKey: ['globalsearch', 'accounts', debounced],
    queryFn: () => accountsApi.list({ q: debounced, limit: PER_BUCKET_LIMIT }),
    enabled,
    staleTime: 30_000,
  });
  const insuranceQ = useQuery({
    queryKey: ['globalsearch', 'insurance', debounced],
    queryFn: () => accountInsuranceApi.list({ q: debounced, limit: PER_BUCKET_LIMIT }),
    enabled,
    staleTime: 30_000,
  });

  const isLoading = enabled && (
    agreementsQ.isFetching || personsQ.isFetching || accountsQ.isFetching || insuranceQ.isFetching
  );
  const totalHits =
    (agreementsQ.data?.items.length ?? 0) +
    (personsQ.data?.items.length ?? 0) +
    (accountsQ.data?.items.length ?? 0) +
    (insuranceQ.data?.items.length ?? 0);

  function go(to: string) {
    combobox.closeDropdown();
    setSearch('');
    inputRef.current?.blur();
    if (!anyDirty()) { navigate(to); return; }
    void confirmDiscardAsync().then((ok) => { if (ok) navigate(to); });
  }

  function handleSubmit(value: string) {
    const [kind, id] = value.split(':');
    if (!kind || !id) return;
    switch (kind) {
      case 'agreement': go(`/agreements/${id}`); break;
      case 'person':    go(`/persons/${id}`); break;
      case 'account':   go(`/accounts/${id}`); break;
      case 'insurance': go(`/account-insurance/${id}`); break;
    }
  }

  return (
    <Combobox store={combobox} onOptionSubmit={handleSubmit} withinPortal>
      <Combobox.Target>
        <TextInput
          ref={inputRef}
          placeholder="Search agreements, persons, accounts… (⌘K)"
          leftSection={<IconSearch size={14} />}
          rightSection={isLoading ? <Loader size={14} /> : null}
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
          }}
          onFocus={() => { if (enabled) combobox.openDropdown(); }}
          onBlur={() => combobox.closeDropdown()}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setSearch('');
              combobox.closeDropdown();
              inputRef.current?.blur();
            }
          }}
          style={{ width: 360 }}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {!enabled && (
            <Combobox.Empty>
              <Text size="xs" c="dimmed">Type at least {MIN_QUERY_LEN} characters…</Text>
            </Combobox.Empty>
          )}
          {enabled && totalHits === 0 && !isLoading && (
            <Combobox.Empty>No matches.</Combobox.Empty>
          )}

          {enabled && (agreementsQ.data?.items.length ?? 0) > 0 && (
            <Combobox.Group label="Agreements">
              {agreementsQ.data!.items.map((a) => (
                <Combobox.Option key={a.id} value={`agreement:${a.id}`}>
                  <Group gap="xs" wrap="nowrap">
                    <IconClipboardList size={14} />
                    <Text size="sm">
                      <Text span fw={500}>{a.code_1}</Text>
                      <Text span c="dimmed"> — {a.client_mnemonic}</Text>
                    </Text>
                  </Group>
                </Combobox.Option>
              ))}
            </Combobox.Group>
          )}

          {enabled && (personsQ.data?.items.length ?? 0) > 0 && (
            <Combobox.Group label="Persons">
              {personsQ.data!.items.map((p) => (
                <Combobox.Option key={p.id} value={`person:${p.id}`}>
                  <Group gap="xs" wrap="nowrap">
                    <IconUsers size={14} />
                    <Text size="sm" fw={500}>{p.display_name}</Text>
                  </Group>
                </Combobox.Option>
              ))}
            </Combobox.Group>
          )}

          {enabled && (accountsQ.data?.items.length ?? 0) > 0 && (
            <Combobox.Group label="Accounts">
              {accountsQ.data!.items.map((a) => (
                <Combobox.Option key={a.id} value={`account:${a.id}`}>
                  <Group gap="xs" wrap="nowrap">
                    <IconBuildingBank size={14} />
                    <Text size="sm">
                      <Text span fw={500}>{a.bank_name}</Text>
                      <Text span c="dimmed"> — {a.internal_num}</Text>
                    </Text>
                  </Group>
                </Combobox.Option>
              ))}
            </Combobox.Group>
          )}

          {enabled && (insuranceQ.data?.items.length ?? 0) > 0 && (
            <Combobox.Group label="Insurance Policies">
              {insuranceQ.data!.items.map((i) => (
                <Combobox.Option key={i.id} value={`insurance:${i.id}`}>
                  <Group gap="xs" wrap="nowrap">
                    <IconShieldCheck size={14} />
                    <Text size="sm">
                      <Text span fw={500}>{i.insurer}</Text>
                      <Text span c="dimmed"> — {i.policy_number}</Text>
                    </Text>
                  </Group>
                </Combobox.Option>
              ))}
            </Combobox.Group>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
